import React, { useState, useRef, useEffect } from 'react';
import { X, Plus } from 'lucide-react';
import './TagInput.css';

interface TagInputProps {
  value: string[];
  onChange: (tags: string[]) => void;
  availableTags: string[];
  placeholder?: string;
  label?: string;
  id?: string;
}

const TagInput: React.FC<TagInputProps> = ({
  value,
  onChange,
  availableTags,
  placeholder = "Add tags...",
  label,
  id
}) => {
  const [inputValue, setInputValue] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [filteredTags, setFilteredTags] = useState<string[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (inputValue) {
      const filtered = availableTags.filter(tag =>
        tag.toLowerCase().includes(inputValue.toLowerCase()) &&
        !value.includes(tag)
      );
      setFilteredTags(filtered);
    } else {
      setFilteredTags(availableTags.filter(tag => !value.includes(tag)));
    }
  }, [inputValue, availableTags, value]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const addTag = (tag: string) => {
    const trimmedTag = tag.trim();
    if (trimmedTag && !value.includes(trimmedTag)) {
      onChange([...value, trimmedTag]);
      setInputValue('');
      setIsOpen(false);
    }
  };

  const removeTag = (tagToRemove: string) => {
    onChange(value.filter(tag => tag !== tagToRemove));
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    setIsOpen(true);
  };

  const handleInputKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (inputValue.trim()) {
        addTag(inputValue);
      }
    } else if (e.key === 'Backspace' && !inputValue && value.length > 0) {
      // Remove last tag when backspace is pressed on empty input
      removeTag(value[value.length - 1]);
    } else if (e.key === 'Escape') {
      setIsOpen(false);
    } else if (e.key === ',') {
      e.preventDefault();
      if (inputValue.trim()) {
        addTag(inputValue);
      }
    }
  };

  const handleInputFocus = () => {
    setIsOpen(true);
  };

  const showCustomOption = inputValue.trim() && 
    !availableTags.includes(inputValue.trim()) && 
    !value.includes(inputValue.trim());

  return (
    <div className="tag-input-container" ref={containerRef}>
      {label && <label htmlFor={id} className="tag-input-label">{label}</label>}
      
      <div className="tag-input-wrapper">
        <div className="tag-input-field">
          {value.map((tag, index) => (
            <div key={index} className="tag-item">
              <span>{tag}</span>
              <button
                type="button"
                className="tag-remove"
                onClick={() => removeTag(tag)}
              >
                <X size={12} />
              </button>
            </div>
          ))}
          
          <input
            ref={inputRef}
            id={id}
            type="text"
            value={inputValue}
            onChange={handleInputChange}
            onKeyDown={handleInputKeyDown}
            onFocus={handleInputFocus}
            placeholder={value.length === 0 ? placeholder : ''}
            className="tag-input"
            autoComplete="off"
          />
        </div>

        {isOpen && (filteredTags.length > 0 || showCustomOption) && (
          <div className="tag-dropdown">
            {showCustomOption && (
              <div
                className="tag-option custom-option"
                onClick={() => addTag(inputValue)}
              >
                <Plus size={14} />
                <span>Add "{inputValue.trim()}"</span>
              </div>
            )}
            {filteredTags.map((tag, index) => (
              <div
                key={index}
                className="tag-option"
                onClick={() => addTag(tag)}
              >
                {tag}
              </div>
            ))}
          </div>
        )}
      </div>
      
      <div className="tag-input-hint">
        Press Enter or comma to add tags
      </div>
    </div>
  );
};

export default TagInput; 